import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import {
  FLAG_ICON_FALLBACK,
  isKnownFlagIcon,
  resolveFlagIcon,
  searchFlagIcons,
} from '@/lib/work-plan-flag-icons';

// Searchable icon picker for staff flags: a small bordered trigger showing the current glyph,
// and a cmdk popover with a search box over the curated Lucide catalog rendered as an 8-column
// grid (keyboard navigation comes from cmdk). Filtering is done by searchFlagIcons — the same
// tested name+keyword match the phone uses conceptually — rather than cmdk's fuzzy scorer, so
// "night" finds moon-star and "key" finds the keyholder glyphs predictably. A value outside
// the catalog (a newer server, a hand-edited slug) still renders the fallback flag and is
// called out under the search box instead of silently becoming something else.
export function IconPickerPopover({ value, onChange, disabled = false, label = 'Icon', toneClassName }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const current = String(value || '').trim() || FLAG_ICON_FALLBACK;
  const Icon = resolveFlagIcon(current);
  const known = isKnownFlagIcon(current);
  const matches = searchFlagIcons(query);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={`${label}: ${current}`}
          title={known ? `${label}: ${current}` : `${label}: ${current} (not in the catalog — shows the fallback flag)`}
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-md border border-border bg-card transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-muted',
            toneClassName,
          )}
        >
          <Icon className="size-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search icons…" value={query} onValueChange={setQuery} />
          <p className="px-3 pb-1 pt-2 text-[10.5px] leading-snug text-muted-foreground">
            Current: <code className="rounded bg-muted px-1 font-mono">{current}</code>
            {known ? null : ' — not in the catalog, shows the fallback flag'}
          </p>
          <CommandList>
            <CommandEmpty>No icons match</CommandEmpty>
            {matches.length > 0 ? (
              // cmdk wraps the items in [cmdk-group-items]; the grid has to land on that wrapper.
              <CommandGroup className="p-2 [&_[cmdk-group-items]]:grid [&_[cmdk-group-items]]:grid-cols-8 [&_[cmdk-group-items]]:gap-1">
                {matches.map((entry) => {
                  const ItemIcon = resolveFlagIcon(entry.name);
                  const selected = entry.name === current;
                  return (
                    <CommandItem
                      key={entry.name}
                      value={entry.name}
                      title={entry.name}
                      aria-pressed={selected}
                      onSelect={() => {
                        onChange(entry.name);
                        setOpen(false);
                      }}
                      className={cn(
                        'flex size-8 cursor-pointer items-center justify-center rounded-md p-0',
                        selected && 'ring-2 ring-primary',
                      )}
                    >
                      <ItemIcon className="size-4" />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
